import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Input } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDateTime } from '@/lib/format'

export function UserDetail() {
  const { t, i18n } = useTranslation('admin')
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const query = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => adminApi.getUser(id),
    enabled: Boolean(id),
  })

  async function run(fn: () => Promise<unknown>) {
    setActionError(null)
    setBusy(true)
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['admin-user', id] })
    } catch (e) {
      setActionError(apiErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const user = query.data?.user
  const auditLogs = query.data?.audit_logs ?? []

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <Link to="/users" className="text-sm text-text-muted hover:text-text">
        {t('common.back')}
      </Link>
      <h1 className="text-2xl font-semibold">{t('user_detail.title')}</h1>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {user ? (
        <>
          <Card>
            <h2 className="mb-3 text-lg font-medium">{t('user_detail.section_profile')}</h2>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-text-muted">{t('user_detail.email')}</dt>
              <dd className="font-mono">{user.email}</dd>
              <dt className="text-text-muted">{t('user_detail.status')}</dt>
              <dd>{t(`statuses.${user.status}`)}</dd>
              <dt className="text-text-muted">{t('user_detail.plan')}</dt>
              <dd className="uppercase">{user.plan}</dd>
              <dt className="text-text-muted">{t('user_detail.kyc')}</dt>
              <dd>{user.kyc_status}</dd>
              <dt className="text-text-muted">{t('user_detail.role')}</dt>
              <dd>{user.role}</dd>
              <dt className="text-text-muted">{t('user_detail.created')}</dt>
              <dd>{formatDateTime(user.created_at, i18n.language)}</dd>
            </dl>
          </Card>

          <Card>
            <h2 className="mb-3 text-lg font-medium">{t('user_detail.section_actions')}</h2>
            {actionError ? <ErrorBanner message={actionError} /> : null}
            <div className="mt-3 flex flex-col gap-3">
              {user.status === 'suspended' ? (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => run(() => adminApi.reactivate(user.id))}
                >
                  {t('user_detail.reactivate')}
                </Button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Input
                    label={t('user_detail.suspend_reason')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    disabled={busy || reason.trim().length === 0}
                    onClick={() => run(() => adminApi.suspend(user.id, reason.trim()))}
                  >
                    {t('user_detail.suspend')}
                  </Button>
                </div>
              )}
              <Button
                variant="tertiary"
                disabled={busy}
                onClick={() => run(() => adminApi.forceLogout(user.id))}
              >
                {t('user_detail.force_logout')}
              </Button>
            </div>
          </Card>

          <Card>
            <h2 className="mb-3 text-lg font-medium">{t('user_detail.section_audit')}</h2>
            {auditLogs.length === 0 ? (
              <p className="text-sm text-text-muted">{t('user_detail.audit_empty')}</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border text-text-muted">
                  <tr>
                    <th className="py-2 font-medium">{t('user_detail.col_action')}</th>
                    <th className="py-2 font-medium">{t('user_detail.col_time')}</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="border-b border-border last:border-0">
                      <td className="py-2 font-mono">{log.action}</td>
                      <td className="py-2 text-text-muted">
                        {formatDateTime(log.timestamp, i18n.language)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      ) : null}
    </div>
  )
}
